import { useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { ProviderType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { logError } from 'lib/logging'

export type UseAiAgentAvailabilityReturn = {
  user: ReturnType<typeof useUser>
  bookableAppointmentTypes: any
  operatories: any
  isSyncing: boolean
  isSyncingSchedules: boolean
  rows: ProviderType[]
  view: `all` | `active` | `inactive`
  activeProviders: ProviderType[]
  inactiveProviders: ProviderType[]
  isDentrix: boolean
  onRefreshClick: () => void
  onSyncSchedulesClick: () => void
  onViewChange: (view: `all` | `active` | `inactive`) => void
  handleProviderStatusChange: (provider: ProviderType, active: boolean) => void
}

export function useAiAgentAvailability(): UseAiAgentAvailabilityReturn {
  const user = useUser()

  const { locationId } = useParams()
  const [bookableAppointmentTypes, setBookableAppointmentTypes] = useState<any>()
  const [operatories, setOperatories] = useState<any>()
  const [isDentrix, setIsDentrix] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSyncingSchedules, setIsSyncingSchedules] = useState(false)
  const [rows, setRows] = useState<ProviderType[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<`all` | `active` | `inactive`>(
    (searchParams.get(`view`) as `all` | `active` | `inactive`) || `active`,
  )

  useEffect(() => {
    if (!locationId) return
    fetch(`/api/scheduling/locations/${locationId}/agent/availabilities`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => {
        setBookableAppointmentTypes(data.bookableAppointmentTypes)
        setOperatories(data.operatories)
        setIsDentrix(data.isDentrix ?? false)
        if (data.providers?.hits) setRows(data.providers.hits)
        else if (data.providers) setRows(data.providers)
      })
      .catch((err) => logError(`Error fetching AI agent availability`, err))
  }, [locationId])

  const activeProviders = useMemo(() => rows?.filter((provider) => provider.deactivated !== true), [rows])
  const inactiveProviders = rows?.filter((provider) => provider.deactivated === true)

  const onRefreshClick = () => {
    setIsSyncing(true)
    fetch(`/api/scheduling/locations/${locationId}/agent/availabilities`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `providers` }),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsSyncing(false)
        if (data?.success && data.providers) {
          if (data.providers.length > 0) {
            setRows((prev) => {
              const existingIds = new Set(prev.map((p) => p.id))
              const actuallyNew = (data.providers as ProviderType[]).filter((p) => !existingIds.has(p.id))
              const newProviderIds = new Set((data.providers as ProviderType[]).map((p) => p.id))
              const existingProviders = prev.filter((p) => !newProviderIds.has(p.id))
              const combined = [...(data.providers as ProviderType[]), ...existingProviders]
              notifySuccess(actuallyNew.length ? `${actuallyNew.length} New Providers Added` : `No New Providers Added`)
              return combined.sort((a, b) => (a.displayName || ``).localeCompare(b.displayName || ``))
            })
          } else {
            notifySuccess(`No New Providers Added`)
          }
          onViewChange(`all`)
        } else if (data?.success) {
          notifySuccess(`PMS Schedules Synced`)
        } else {
          notifyError(`Error syncing providers`)
        }
      })
  }

  const onSyncSchedulesClick = () => {
    setIsSyncingSchedules(true)
    fetch(`/api/scheduling/locations/${locationId}/agent/availabilities`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `schedules` }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) {
          notifySuccess(`PMS Schedules Synced`)
        } else {
          notifyError(`Error syncing schedules`)
        }
        setIsSyncingSchedules(false)
      })
  }

  const onViewChange = (v: `all` | `active` | `inactive`) => {
    setView(v)
    setSearchParams({ view: v })
  }

  const handleProviderStatusChange = (provider: ProviderType, active: boolean) => {
    setRows((prev) => prev.map((p) => (p.id === provider.id ? { ...p, deactivated: !active } : p)))
  }

  return {
    user,
    bookableAppointmentTypes,
    operatories,
    isSyncing,
    isSyncingSchedules,
    rows,
    view,
    activeProviders,
    inactiveProviders,
    isDentrix,
    onRefreshClick,
    onSyncSchedulesClick,
    onViewChange,
    handleProviderStatusChange,
  }
}
